import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  InfoWindow,
  MarkerClusterer
} from '@react-google-maps/api';
import styled from 'styled-components';

// Default center: Fort Myers, FL
const DEFAULT_CENTER = { lat: 26.6406, lng: -81.8723 };
const DEFAULT_ZOOM = 10;

// Marker colors by listing type
const MARKER_COLORS = {
  farm: '#4CAF50',     // Green
  market: '#FF9800',   // Orange
  csa: '#2196F3',      // Blue
  default: '#9E9E9E'   // Gray
};

// Clean map style to reduce visual clutter
const mapStyles = [
  {
    featureType: 'poi',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }]
  },
  {
    featureType: 'poi.business',
    stylers: [{ visibility: 'off' }]
  },
  {
    featureType: 'transit',
    elementType: 'labels.icon',
    stylers: [{ visibility: 'off' }]
  },
  {
    featureType: 'landscape',
    elementType: 'geometry',
    stylers: [{ color: '#f5f5dc' }]
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#c9e9f6' }]
  }
];

// Styled components for the map container and info window
const MapContainer = styled.div`
  width: 100%;
  height: ${props => props.height || '500px'};
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    height: ${props => props.mobileHeight || '350px'};
  }
`;

const LoadingContainer = styled.div`
  width: 100%;
  height: ${props => props.height || '500px'};
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f5dc;
  border-radius: 8px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #e0e0e0;
  border-top-color: #4CAF50;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ErrorContainer = styled.div`
  width: 100%;
  height: ${props => props.height || '500px'};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #fff8f0;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
`;

const ErrorMessage = styled.p`
  color: #8b4513;
  font-size: 16px;
  margin-bottom: 10px;
`;

const InfoWindowContent = styled.div`
  padding: 12px;
  min-width: 200px;
  max-width: 280px;
  font-family: inherit;
`;

const InfoWindowTitle = styled.h4`
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: #2d3436;
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 10px;
  background-color: ${props => {
    switch (props.type?.toLowerCase()) {
      case 'farm': return '#e8f5e9';
      case 'market': return '#fff3e0';
      case 'csa': return '#e3f2fd';
      default: return '#f5f5f5';
    }
  }};
  color: ${props => {
    switch (props.type?.toLowerCase()) {
      case 'farm': return '#2e7d32';
      case 'market': return '#e65100';
      case 'csa': return '#1565c0';
      default: return '#616161';
    }
  }};
`;

const InfoWindowAddress = styled.p`
  margin: 0 0 12px 0;
  font-size: 13px;
  color: #636e72;
  line-height: 1.4;
`;

const ViewDetailsLink = styled(Link)`
  display: inline-block;
  padding: 8px 16px;
  background-color: #4CAF50;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #388E3C;
    color: white;
  }
`;

// SVG marker icon generator
const createMarkerIcon = (color) => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40">
      <path fill="${color}" stroke="#ffffff" stroke-width="2" d="M16 2C8.268 2 2 8.268 2 16c0 10.5 14 22 14 22s14-11.5 14-22c0-7.732-6.268-14-14-14z"/>
      <circle fill="#ffffff" cx="16" cy="16" r="6"/>
    </svg>
  `;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

// Cluster styles
const clusterStyles = [
  {
    textColor: 'white',
    height: 40,
    width: 40,
    backgroundPosition: 'center center'
  },
  {
    textColor: 'white',
    height: 50,
    width: 50,
    backgroundPosition: 'center center'
  },
  {
    textColor: 'white',
    height: 60,
    width: 60,
    backgroundPosition: 'center center'
  }
];

// Custom cluster icon generator
const clusterIconGenerator = (cluster) => {
  const count = cluster.getMarkers().length;
  let size = 40;
  let index = 0;

  if (count >= 10 && count < 50) {
    size = 50;
    index = 1;
  } else if (count >= 50) {
    size = 60;
    index = 2;
  }

  return {
    text: String(count),
    index: index + 1,
    title: `${count} listings in this area`,
    className: 'map-cluster-icon'
  };
};

// Main MapView component
const MapView = ({
  listings = [],
  center,
  zoom = DEFAULT_ZOOM,
  height = '500px',
  onMarkerClick
}) => {
  const [map, setMap] = useState(null);
  const [selectedListing, setSelectedListing] = useState(null);
  const [userLocation, setUserLocation] = useState(null);

  // Load Google Maps API
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || ''
  });

  // Get user location on mount
  useEffect(() => {
    if (navigator.geolocation && !center) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        () => {
          // Silently fail and use default center
          console.log('Unable to get user location, using default center');
        }
      );
    }
  }, [center]);

  // Calculate map center
  const mapCenter = useMemo(() => {
    if (center) return center;
    if (userLocation) return userLocation;
    return DEFAULT_CENTER;
  }, [center, userLocation]);

  // Map options
  const mapOptions = useMemo(() => ({
    styles: mapStyles,
    disableDefaultUI: false,
    zoomControl: true,
    fullscreenControl: true,
    mapTypeControl: false,
    streetViewControl: false,
    scaleControl: true
  }), []);

  // Handle map load
  const onLoad = useCallback((map) => {
    setMap(map);

    // Fit bounds to markers if there are listings
    if (listings.length > 1) {
      const bounds = new window.google.maps.LatLngBounds();
      listings.forEach((listing) => {
        if (listing.coordinates?.lat && listing.coordinates?.lng) {
          bounds.extend({
            lat: listing.coordinates.lat,
            lng: listing.coordinates.lng
          });
        }
      });
      map.fitBounds(bounds);
    }
  }, [listings]);

  // Handle map unmount
  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  // Handle marker click
  const handleMarkerClick = useCallback((listing) => {
    setSelectedListing(listing);
    if (onMarkerClick) {
      onMarkerClick(listing);
    }
  }, [onMarkerClick]);

  // Close info window
  const handleInfoWindowClose = useCallback(() => {
    setSelectedListing(null);
  }, []);

  // Get marker color based on listing type
  const getMarkerColor = (type) => {
    const normalizedType = type?.toLowerCase();
    return MARKER_COLORS[normalizedType] || MARKER_COLORS.default;
  };

  // Filter listings with valid coordinates
  const validListings = useMemo(() => {
    return listings.filter(
      (listing) =>
        listing.coordinates &&
        typeof listing.coordinates.lat === 'number' &&
        typeof listing.coordinates.lng === 'number'
    );
  }, [listings]);

  // Loading state
  if (!isLoaded) {
    return (
      <LoadingContainer height={height}>
        <LoadingSpinner />
      </LoadingContainer>
    );
  }

  // Error state
  if (loadError) {
    return (
      <ErrorContainer height={height}>
        <ErrorMessage>Unable to load the map. Please try again later.</ErrorMessage>
      </ErrorContainer>
    );
  }

  return (
    <MapContainer height={height}>
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={mapCenter}
        zoom={zoom}
        options={mapOptions}
        onLoad={onLoad}
        onUnmount={onUnmount}
      >
        {/* Marker Clusterer for grouped markers */}
        <MarkerClusterer
          calculator={clusterIconGenerator}
          styles={clusterStyles}
          minimumClusterSize={3}
          gridSize={60}
        >
          {(clusterer) =>
            validListings.map((listing) => (
              <Marker
                key={listing.id}
                position={{
                  lat: listing.coordinates.lat,
                  lng: listing.coordinates.lng
                }}
                clusterer={clusterer}
                icon={{
                  url: createMarkerIcon(getMarkerColor(listing.type)),
                  scaledSize: new window.google.maps.Size(32, 40),
                  anchor: new window.google.maps.Point(16, 40)
                }}
                onClick={() => handleMarkerClick(listing)}
                title={listing.name}
              />
            ))
          }
        </MarkerClusterer>

        {/* Info Window for selected listing */}
        {selectedListing && (
          <InfoWindow
            position={{
              lat: selectedListing.coordinates.lat,
              lng: selectedListing.coordinates.lng
            }}
            onCloseClick={handleInfoWindowClose}
            options={{
              pixelOffset: new window.google.maps.Size(0, -40)
            }}
          >
            <InfoWindowContent>
              <InfoWindowTitle>{selectedListing.name}</InfoWindowTitle>
              <TypeBadge type={selectedListing.type}>
                {selectedListing.type || 'Listing'}
              </TypeBadge>
              {selectedListing.address && (
                <InfoWindowAddress>{selectedListing.address}</InfoWindowAddress>
              )}
              <ViewDetailsLink to={`/listing/${selectedListing.slug || selectedListing.id}`}>
                View Details
              </ViewDetailsLink>
            </InfoWindowContent>
          </InfoWindow>
        )}
      </GoogleMap>
    </MapContainer>
  );
};

export default MapView;
